import { findIndex } from 'lodash';
import React, { useContext, useEffect, useMemo } from 'react';
import { UserContext } from '../../../auth/UserContext';
import Loading from '../../../components/Loading';
import { getApplicationById } from '../../../nav/apps';
import useDefaultRoute from '../../../nav/useDefaultRoute';
import { log } from '../../../utils/commonUtils';
import useHistory, { ChangeRoute } from '../../../utils/useHistory';
import { DEEP_LINK_UI_QUERY_PARAM_KEY } from '../../constants';
import { useDeepLinkContext } from '../hooks/useDeepLinkContext';

export default function DeepLinkResolver() {
    const { isAuthenticated, userProfile, application, setApplication } = useContext(UserContext);
    const deepLinkContext = useDeepLinkContext();
    const { routeParams, logOut } = useHistory();
    const { url } = useDefaultRoute();
    const targetApplicationId = routeParams?.appId as string | undefined;

    const sanitizedSearch = useMemo(() => {
        const params = new URLSearchParams(window.location.search);
        params.delete(DEEP_LINK_UI_QUERY_PARAM_KEY);

        const nextSearch = params.toString();
        return nextSearch ? `?${nextSearch}` : '';
    }, []);

    const targetApplication = useMemo(() => {
        if (!targetApplicationId) {
            return undefined;
        }

        return getApplicationById(targetApplicationId);
    }, [targetApplicationId]);

    const hasTargetAppAccess = useMemo(() => {
        if (!userProfile || !targetApplication) {
            return false;
        }

        return (
            findIndex(
                userProfile.memberships,
                (membership) =>
                    findIndex(membership.access, (access) => targetApplication.name === access.application) > -1
            ) > -1
        );
    }, [targetApplication, userProfile]);

    useEffect(() => {
        if (!isAuthenticated) {
            log('[DeepLinkResolver] User accessing secure route without being authenticated. Logging out...');
            logOut();
        }
    }, [isAuthenticated, logOut]);

    useEffect(() => {
        if (!isAuthenticated || !userProfile || !targetApplication || !hasTargetAppAccess) {
            return;
        }

        if (!application || application.id !== targetApplication.id) {
            log('[DeepLinkResolver] setting application:', targetApplication);
            setApplication(targetApplication);
        }
    }, [application, hasTargetAppAccess, isAuthenticated, setApplication, targetApplication, userProfile]);

    if (!isAuthenticated) {
        return <ChangeRoute path={'/login'} />;
    }

    if (!userProfile) {
        return <Loading />;
    }

    if (!targetApplication || !hasTargetAppAccess) {
        if (url) {
            return <ChangeRoute path={url} />;
        }

        return <ChangeRoute path={'/login'} />;
    }

    if (!application || application.id !== targetApplication.id) {
        return <Loading />;
    }

    const resolvedDeepLinkRoutePath = deepLinkContext.getResolvedDeepLinkRoutePath();

    if (!resolvedDeepLinkRoutePath) {
        const resolvedAppHomePath = `${application.home}${sanitizedSearch}`;
        return <ChangeRoute path={resolvedAppHomePath} />;
    }

    const resolvedDeepLinkPath = `${application.home}${resolvedDeepLinkRoutePath}${sanitizedSearch}`;
    return <ChangeRoute path={resolvedDeepLinkPath} />;
}
